// Shared contracts between the sim world and the renderer. Both sides import
// from here; changes should be rare and deliberate.

export interface AntState {
  id: number;
  x: number;
  y: number;   // snapped to terrain each tick
  z: number;
  vx: number;  // horizontal velocity (m/s)
  vz: number;
  heading: number; // radians, 0 = +X, CCW positive
  speed: number;   // |horizontal velocity|, cached for shader gait
  phase: number;   // per-ant gait phase offset [0, 2π)
  caste: 0 | 1;    // 0 worker, 1 soldier — phase 5+
  carrying: boolean;        // true if currently hauling food back to nest
  task: AntTask;            // current behavior mode
  role: AntRole;            // normal forager, player-controlled, or follower of player
  goal: { x: number; z: number } | null; // only used by player role; click-to-move target
  /** Seconds remaining in the enter-nest cycle (dive, hide, re-emerge). 0 = surface. */
  nestCycleT: number;
  /** Total duration of the current nest cycle, used by the renderer for Y bob. */
  nestCycleDur: number;
  /** Which colony (and team) this ant belongs to. 0 = red, 1 = black. */
  team: 0 | 1;
  /** False after losing a fight — skipped by sim, rendered as a corpse. */
  alive: boolean;
  /** Opponent ant id if currently locked in a fight, else -1. (Kept for compat; unused now.) */
  fightWith: number;
  /** Seconds remaining in the current fight. (Kept for compat; unused now.) */
  fightT: number;
  /** Seconds since death — drives corpse sink + fade in the renderer. */
  deathT: number;
  /** Hit points. 1.0 = full health; ant dies at <= 0. */
  hp: number;
  /** Set each frame if any enemy is within melee range this tick. */
  attacking: boolean;
  /** Hunger in [0, 1]. 0 = just ate, 1 = starving (dies). */
  hunger: number;
  /** Per-second hunger increase. Player is 1/120, regular is 1/60. */
  hungerRate: number;
  /** Elapsed time (seconds) when this ant last gifted food; -Infinity if never. */
  lastGiftT: number;
  /** Seconds remaining on the gift-flash one-shot animation trigger (renderer drives). */
  giftFlashT: number;
  /** Social caste — drives AI branch + scale + render tint. */
  casteType: AntCaste;
  /** Stockpile value of the food currently being carried. */
  carryValue: number;
}

export type AntTask = 'search' | 'return';
export type AntRole = 'normal' | 'player' | 'follower';
export type AntCaste = 'worker' | 'soldier' | 'queen' | 'nurse' | 'scout' | 'breeder';

/** Numeric role id written into the `aRole` per-instance shader attribute. */
export const ROLE_ID: Record<AntRole, number> = {
  normal: 0,
  player: 1,
  follower: 2,
};

/** Colony-level state (stockpile, queen status, caste policy). */
export interface ColonySnapshot {
  team: 0 | 1;
  alive: boolean;
  stockpile: number;
  eggs: number;
  larvae: number;
  population: Record<AntCaste, number>;  // alive per-caste counts
  hasQueen: boolean;
  /** Target ratios — the queen biases egg casting toward undersupplied roles. */
  ratios: CasteRatios;
}

export type CasteRatios = Record<Exclude<AntCaste, 'queen'>, number>;

export interface PlayerControl {
  /** Id of the player-controlled ant. Guaranteed to exist after construction. */
  readonly playerId: number;
  /** Set (or clear) the player ant's navigation goal in world coords. */
  setPlayerGoal(x: number, z: number): void;
  clearPlayerGoal(): void;
  /** Promote `n` normal ants to followers of the player. Capped at available normals. */
  summonFollowers(n: number): number;
  /** Demote a fraction (0..1) of followers back to normal. Returns count released. */
  releaseFollowers(fraction: number): number;
  /** Recall every same-team ant to the nest and keep them underground until
   *  called again. Toggles each press. */
  toggleNestRecall(): boolean;
}

export interface NestConfig {
  x: number;
  z: number;
  radius: number; // metres — "arrived home" threshold
}

export type FoodKind = 'pile' | 'corpse';

export interface FoodItem {
  id: number;
  x: number;
  z: number;
  amount: number;       // remaining pickups
  value: number;        // stockpile units per pickup (sugar >> ant corpse)
  kind: FoodKind;
}

export interface FoodField {
  readonly items: FoodItem[];
  /** Add a food source. `value` is per-pickup stockpile yield. */
  spawn(x: number, z: number, amount: number, value?: number, kind?: FoodKind): number;
  /** If an ant at (x, z) is within `grabRadius` of a food item with amount>0,
   *  decrement it and return the item. Returns undefined otherwise. */
  tryPickup(x: number, z: number, grabRadius: number): FoodItem | undefined;
}

export type PheromoneChannel = 0 | 1; // 0 = food-trail, 1 = home-trail

export interface Pheromones {
  /** Deposit `amount` into `channel` at world (x, z). Splats a small kernel. */
  deposit(x: number, z: number, channel: PheromoneChannel, amount: number): void;
  /** Scalar concentration at world (x, z). Bilinear sample. */
  sample(x: number, z: number, channel: PheromoneChannel): number;
  /** 2D gradient (dC/dx, dC/dz) at world (x, z). Used for trail following. */
  gradient(x: number, z: number, channel: PheromoneChannel, out: [number, number]): void;
  /** Advance the field: decay + (optional) diffusion. */
  update(dt: number): void;
  /** Raw grid accessor for debug overlay. */
  readonly size: number;   // cells per side
  readonly extent: number; // world metres covered
  readonly data: [Float32Array, Float32Array]; // [food, home]
}

export const FORAGE_CONFIG = {
  grabRadius: 0.6,         // metres — how close an ant must get to pick up food
  depositFoodTrail: 3.0,   // per-second amount deposited while returning
  depositHomeTrail: 1.5,   // per-second amount deposited while searching
  followStrength: 1.6,     // how strongly ants steer along gradient
  senseDistance: 0.8,      // metres — how far ahead ants sample
  senseAngle: 0.7,         // radians off-heading for left/right antennae sampling
};

export interface AntSimWorld {
  readonly ants: AntState[];
  readonly count: number;
  /** Add a new ant at world (x, z). Returns its id. */
  spawn(x: number, z: number): number;
  /** Advance the sim by dt seconds; elapsed is total wall time in seconds. */
  update(dt: number, elapsed: number): void;
}

/** Rough ant body dimensions (meters) — used for mesh + shader. */
export const ANT_DIMS = {
  totalLength: 0.012,   // 12mm ant — actual SimAnt-scale
  bodyScale: 6.0,        // display scale multiplier so they read at god view
  headRadius: 0.0018,
  thoraxLength: 0.004,
  gasterRadius: 0.0028,
  legLength: 0.006,
  antennaLength: 0.0045,
};

/** Visible size = totalLength * bodyScale (~7cm on-screen). */
export const ANT_VISUAL_LENGTH = ANT_DIMS.totalLength * ANT_DIMS.bodyScale;
